import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout';
import { useSessionStore } from '@/stores/sessionStore';
import { ToastContainer } from '@/components/common';

// Lazy-loaded route pages
const Overview = React.lazy(() => import('@/pages/Overview'));
const RiskAppetite = React.lazy(() => import('@/pages/RiskAppetite'));
const ScenarioResilience = React.lazy(() => import('@/pages/ScenarioResilience'));
const KeyRiskIndicators = React.lazy(() => import('@/pages/KeyRiskIndicators'));
const ControlEffectiveness = React.lazy(() => import('@/pages/ControlEffectiveness'));
const CultureConduct = React.lazy(() => import('@/pages/CultureConduct'));
const AIInsights = React.lazy(() => import('@/pages/AIInsights'));
const ArbiterChallenges = React.lazy(() => import('@/pages/ArbiterChallenges'));
const ActionsEscalations = React.lazy(() => import('@/pages/ActionsEscalations'));
const CommitteePack = React.lazy(() => import('@/pages/CommitteePack'));
const NotFound = React.lazy(() => import('@/pages/NotFound'));

// Loading fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      <p className="mt-4 text-slate-600 dark:text-slate-400">Loading...</p>
    </div>
  </div>
);

function App() {
  const theme = useSessionStore((state) => state.theme);

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  return (
    <BrowserRouter>
      <ToastContainer />
      <Routes>
        <Route path="/" element={<Navigate to="/brc/overview" replace />} />

        <Route path="/brc" element={<MainLayout />}>
          <Route index element={<Navigate to="overview" replace />} />

          <Route
            path="overview"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <Overview />
              </React.Suspense>
            }
          />

          <Route
            path="risk-appetite"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <RiskAppetite />
              </React.Suspense>
            }
          />

          <Route
            path="scenario-resilience"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <ScenarioResilience />
              </React.Suspense>
            }
          />

          <Route
            path="key-risk-indicators"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <KeyRiskIndicators />
              </React.Suspense>
            }
          />

          <Route
            path="control-effectiveness"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <ControlEffectiveness />
              </React.Suspense>
            }
          />

          <Route
            path="culture-conduct"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <CultureConduct />
              </React.Suspense>
            }
          />

          <Route
            path="ai-insights"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <AIInsights />
              </React.Suspense>
            }
          />

          <Route
            path="arbiter-challenges"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <ArbiterChallenges />
              </React.Suspense>
            }
          />

          <Route
            path="actions-escalations"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <ActionsEscalations />
              </React.Suspense>
            }
          />

          <Route
            path="committee-pack"
            element={
              <React.Suspense fallback={<LoadingFallback />}>
                <CommitteePack />
              </React.Suspense>
            }
          />
        </Route>

        <Route
          path="*"
          element={
            <React.Suspense fallback={<LoadingFallback />}>
              <NotFound />
            </React.Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
